/**
 * Cauthmodal Component
 * Authentication modal with social login and email options
 * Supports multiple states: initial, email sent, activation complete, error
 */

'use client';

import type { CSSProperties, ReactElement } from 'react';
import { useRef } from 'react';
import { AuthModalState } from '@/types/auth-types';
import { useViewport } from '@/hooks/dom';
import { Viewport } from '@/theme/breakpoints';
import { CauthmodalContent } from './CauthmodalContent';
import { CauthmodalFrame } from './CauthmodalFrame';
import { getAuthModalLayout } from './authModalLayout';
import { useCauthmodalEscapeDismiss } from './useCauthmodalEscapeDismiss';
import { useCauthmodalState } from './useCauthmodalState';

export interface CauthmodalProps {
  onClose?: () => void;
  initialState?: AuthModalState;
  className?: string;
  style?: CSSProperties;
  /** Padding for the container (CSS padding value) */
  padding?: string;
}

export function Cauthmodal({
  onClose,
  initialState = AuthModalState.Initial,
  className,
  style,
  padding = '48px 40px',
}: CauthmodalProps): ReactElement {
  const { viewport } = useViewport();
  const isMobile = viewport === Viewport.Mobile;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const authState = useCauthmodalState({ initialState });
  useCauthmodalEscapeDismiss({ onClose, authState });

  const modalLayout = getAuthModalLayout({ isMobile, padding });

  return (
    <CauthmodalFrame
      wrapperRef={wrapperRef}
      className={className}
      style={style}
      modalLayout={modalLayout}
      hasOpenedTerms={authState.hasOpenedTerms}
      showTerms={authState.showTerms}
      onCloseTerms={authState.handleCloseTerms}
      hasOpenedPolicy={authState.hasOpenedPolicy}
      showPolicy={authState.showPolicy}
      onClosePolicy={authState.handleClosePolicy}
    >
      <CauthmodalContent
        state={authState}
        onClose={onClose}
      />
    </CauthmodalFrame>
  );
}
